import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';


/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — data model utilities
   The document is now data-driven: inline edits write to a plain
   `data` object (the merge context), never to raw HTML. Totals
   recompute from line items. Exposes PBModel.
   ───────────────────────────────────────────────────────────── */

  const clone = (o) => (typeof structuredClone === 'function' ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

  // get a value by dotted path: "company.name", "items.3.qty"
  function getByPath(obj, path) {
    return path.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
  }

  // immutably set a value by dotted path; returns a new object
  function setByPath(obj, path, val) {
    const parts = path.split('.');
    const next = Array.isArray(obj) ? obj.slice() : { ...obj };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      const child = cur[k];
      cur[k] = Array.isArray(child) ? child.slice() : { ...child };
      cur = cur[k];
    }
    cur[parts[parts.length - 1]] = val;
    return next;
  }

  // recompute line amounts + totals from items (sales docs only)
  function recomputeTotals(data) {
    if (!Array.isArray(data.items) || !data.items.length || !('price' in data.items[0])) return data;
    let sub = 0, tax = 0, disc = 0, grossSum = 0;
    const items = data.items.map((it) => {
      const q = Number(it.qty) || 0, p = Number(it.price) || 0;
      const gross = q * p;
      const dAmt = gross * ((Number(it.disc) || 0) / 100);
      const net = gross - dAmt;
      const tAmt = net * ((Number(it.tax) || 0) / 100);
      sub += net; tax += tAmt; disc += dAmt; grossSum += gross;
      // amount = net (legacy); taxable/taxAmt/total are extra fields KSA-style
      // bilingual templates bind to — harmless to templates that ignore them.
      return { ...it, amount: net, gross, discAmt: dAmt, taxable: net, taxAmt: tAmt, total: net + tAmt };
    });
    const out = { ...data, items };
    if (data.totals) {
      out.totals = { ...data.totals, subtotal: sub, discount: disc, tax, total: sub + tax };
      // ZATCA / KSA-style aliases — only populated when the sample opts in
      // (its totals object carries a `gross` or `exclVat` key).
      if ('gross' in data.totals || 'exclVat' in data.totals) {
        out.totals.gross = grossSum;
        out.totals.exclVat = sub;
        out.totals.vatAmount = tax;
        out.totals.inclVat = sub + tax;
        out.totals.balanceDue = sub + tax;
      }
    }
    return out;
  }

  // parse a number out of an edited string ("AED 2,150.00" → 2150, "5%" → 5)
  function parseNum(str) {
    const n = parseFloat(String(str).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  // apply one inline edit → new data object
  function applyEdit(data, path, type, rawText) {
    let val = rawText;
    if (type === 'num' || type === 'money') val = parseNum(rawText);
    let next = setByPath(data, path, val);
    // recompute when a line-item money/qty field changed
    if (/^items\.\d+\.(qty|price|disc|tax)$/.test(path)) next = recomputeTotals(next);
    return next;
  }

  // line-item row ops
  function addItem(data) {
    if (!Array.isArray(data.items)) return data;
    const blank = { sku: 'NEW-000', name: 'New line item', detail: '', qty: 1, unit: 'pcs', price: 0, tax: 5, disc: 0 };
    const items = [...data.items, { ...blank }];
    return recomputeTotals({ ...data, items });
  }
  function removeItem(data, idx) {
    const items = data.items.filter((_, i) => i !== idx);
    return recomputeTotals({ ...data, items });
  }
  function moveItem(data, idx, dir) {
    const items = data.items.slice();
    const j = idx + dir;
    if (j < 0 || j >= items.length) return data;
    [items[idx], items[j]] = [items[j], items[idx]];
    return recomputeTotals({ ...data, items });
  }

  export const PBModel = { clone, getByPath, setByPath, recomputeTotals, parseNum, applyEdit, addItem, removeItem, moveItem };

